// CanvasEmptyState — friendly nudge shown when the canvas has zero
// blocks. Replaces the "dead-space" feel of an empty workspace with a
// clear next-action. Pointer-events:none so it doesn't intercept drag.

import { Layers, MousePointer2, Sparkles } from 'lucide-react';

interface Props {
  /** True when the user has nothing on the canvas. */
  isEmpty: boolean;
  /** Optional: open the Welcome/Wizard dialog. */
  onOpenWizard?: () => void;
  /**
   * Visible viewport of the scroll-container, in scroll-content
   * coordinates. When provided, the overlay is centered on the
   * currently-visible area instead of `inset-0`. The empty-state lives
   * inside an `overflow-auto` canvas whose content box is far larger
   * than the viewport, so a plain `inset-0` anchors to the scrolled-away
   * top-left corner and drifts off-screen once the canvas auto-fits.
   */
  viewport?: {
    scrollLeft: number;
    scrollTop: number;
    width: number;
    height: number;
  };
}

export function CanvasEmptyState({ isEmpty, onOpenWizard, viewport }: Props) {
  if (!isEmpty) return null;

  // Center on the visible viewport when we know where it is; otherwise
  // fall back to `inset-0` (used by unit-tests rendering in isolation).
  const positioning = viewport
    ? {
        className:
          'absolute flex items-center justify-center pointer-events-none',
        style: {
          left: viewport.scrollLeft + viewport.width / 2,
          top: viewport.scrollTop + viewport.height / 2,
          transform: 'translate(-50%, -50%)',
          // Fixed width: otherwise shrink-to-fit narrows the box when
          // `left` nears the containing block's right edge (text wraps
          // word-by-word depending on horizontal scroll).
          width: '24rem',
          maxWidth: 'calc(100vw - 2rem)',
        } as React.CSSProperties,
      }
    : {
        className:
          'absolute inset-0 flex items-center justify-center pointer-events-none',
        style: undefined,
      };

  return (
    <div
      className={positioning.className}
      style={positioning.style}
      data-testid="canvas-empty-state"
      aria-hidden="false"
    >
      <div className="max-w-sm text-center space-y-4 px-6 pointer-events-auto select-none">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/60 border-2 border-dashed border-muted-foreground/30">
          <Layers className="w-7 h-7 text-muted-foreground" />
        </div>

        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Tom canvas</h2>
          <p className="text-sm text-muted-foreground">
            Börja genom att dra in ett block från sidopanelen, eller starta med en mall.
          </p>
        </div>

        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <MousePointer2 className="w-3.5 h-3.5" />
            Tips: dubbelklicka på canvas för att skapa ett block.
          </div>
          {onOpenWizard && (
            <button
              type="button"
              onClick={onOpenWizard}
              className="inline-flex items-center gap-1.5 text-primary hover:underline font-medium"
              data-testid="canvas-empty-open-wizard"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Eller använd Welcome-guiden →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
